import { Button, Col, Container, Label, Loader, Radio, RadioGroup, Row, SelectInput, TextInput, themes, } from '@digitools/honest-react';
import React, { useEffect, useState } from 'react';
import { UA_DURATION_HOURS, UA_HOURS, UA_MINUTES, UA_TIME_OFF_REASONS, UA_TIME_OFF_TYPES, } from '../constants/UAConstants';
//@ts-ignore - Relevant isn't in 'informed' typescript
import { Relevant, useFormApi, useFormState } from 'informed';
import moment from 'moment';
import { formatDate, formatTime, GetAssociatedLeaves } from '../api/UnplannedAbsenceApi';
import styled from 'styled-components';
import { addDays, differenceInHours, format, isAfter, isBefore, isValid, parse, subDays } from 'date-fns';
import { getEnvVariable } from 'src/utils/envUtils';
import LanguageToggle from 'src/utils/LanguageToggle';
const UnplannedAbsenceForm = ({ hasAbsences = false, initialAbsence, onCancel }) => {
    const formState = useFormState();
    const formApi = useFormApi();
    const lowerDateBoundary = subDays(new Date(), 5).setHours(0, 0, 0, 0);
    const upperDateBoundary = addDays(new Date(), 5);
    const [dateTimeErrorMsg, setDateTimeErrorMsg] = useState();
    const [startTimeLabel, setStartTimeLabel] = useState('Start time');
    const [endTimeLabel, setEndTimeLabel] = useState('End time');
    const [startTimeQuestion, setStartTimeQuestion] = useState('When would your shift have started?');
    const [endTimeQuestion, setEndTimeQuestion] = useState('When would your shift have ended?');
    const { loading: leavesLoading, data: leavesData, error: leavesError, get: getLeaves } = GetAssociatedLeaves();
    const [leaves, setLeaves] = useState([]);
    const validateDurationMinutes = (value) => {
        if (!value) {
            return <LanguageToggle en='Duration minutes is required.' es='Minutos de duración es obligatorio'/>;
        }
        const durationHours = formApi.getValue('durationHours');
        if (durationHours === '00' && value === '00') {
            return <LanguageToggle en='00 hours and 00 minutes is not a valid duration. If you want to delete an absence, use the Action buttons above.' es='00 horas y 00 minutos no es una duración válida. Si desea eliminar una ausencia, use los botones de Acción de arriba.'/>;
        }
        return undefined;
    };
    const validateTimeoffDate = (timeOffdate) => {
        const enteredDate = parse(timeOffdate, 'MM/dd/yyyy', new Date());
        if (enteredDate && isValid(enteredDate)) {
            if (isBefore(enteredDate, lowerDateBoundary) || isAfter(enteredDate, upperDateBoundary)) {
                const lowBound = format(lowerDateBoundary, 'MM/dd/yyyy');
                const upperBound = format(upperDateBoundary, 'MM/dd/yyyy');
                return <LanguageToggle en={`Date is not between ${lowBound} and ${upperBound}`} es={`La fecha no está entre ${lowBound} y ${upperBound}`}/>;
            }
        }
        else {
            return <LanguageToggle en='Please enter a valid date (mm/dd/yyyy)' es='Ingrese una fecha válida (mm/dd/aaaa)'/>;
        }
    };
    useEffect(() => {
        if (formState.values.spansMidnight && formState.values.absType === 'OUT') {
            setStartTimeLabel('Start time');
            setEndTimeLabel('End time');
            setStartTimeQuestion('When would your shift have started?');
            setEndTimeQuestion('When would your shift have ended?');
        }
        else if (formState.values.absType === 'LATE') {
            setStartTimeLabel('Normal start');
            setEndTimeLabel('Estimated arrival');
            setStartTimeQuestion('When would you have started work?');
            setEndTimeQuestion('When did you actually arrive?');
        }
        else if (['EARLYNORTN', 'EARLYRTN'].includes(formState.values.absType)) {
            setStartTimeLabel('Depart time');
            setStartTimeQuestion('When are you leaving work early?');
            if (formState.values.absType === 'EARLYNORTN') {
                setEndTimeLabel('Regular end');
                setEndTimeQuestion('When would you normally leave work?');
            }
            else if (formState.values.absType === 'EARLYRTN') {
                setEndTimeLabel('Estimated return');
                setEndTimeQuestion('When will you return to work?');
            }
        }
        if (formState.values.absType !== 'OUT') {
            formApi.setValue('startTimeHours', undefined);
            formApi.setValue('startTimeMinutes', undefined);
            formApi.setValue('endTimeHours', undefined);
            formApi.setValue('endTimeMinutes', undefined);
        }
    }, [formState.values.spansMidnight, formState.values.absType]);
    const validateEndMinutes = () => {
        const startTimeHours = formApi.getValue('startTimeHours');
        const startTimeMinutes = formApi.getValue('startTimeMinutes');
        const endTimeHours = formApi.getValue('endTimeHours');
        const endTimeMinutes = formApi.getValue('endTimeMinutes');
        const spansMidnight = formApi.getValue('spansMidnight');
        const absType = formApi.getValue('absType');
        if (startTimeHours && startTimeMinutes && endTimeHours && endTimeMinutes) {
            let startTimeStamp = parse(`${startTimeHours}:${startTimeMinutes}`, 'HH:mm', new Date());
            let endTimeStamp = parse(`${endTimeHours}:${endTimeMinutes}`, 'HH:mm', new Date());
            if (spansMidnight && absType === 'OUT') {
                endTimeStamp = addDays(endTimeStamp, 1);
                if (differenceInHours(endTimeStamp, startTimeStamp) >= 24) {
                    setDateTimeErrorMsg('Absence should be fewer than 24 hours');
                    return <LanguageToggle en='End time minutes: absence should be fewer than 24 hours' es='Minutos de hora de fin: la ausencia debe ser menor de 24 horas'/>;
                }
            }
            else {
                if (isBefore(endTimeStamp, startTimeStamp)) {
                    if (['EARLYRTN', 'EARLYNORTN'].includes(absType)) {
                        setDateTimeErrorMsg(`${startTimeLabel} must be before ${endTimeLabel.toLowerCase()}`);
                        return <LanguageToggle en={`End time minutes: ${startTimeLabel} must be before ${endTimeLabel.toLowerCase()}`} es={`Minutos de hora de fin: ${startTimeLabel} debe ser antes de ${endTimeLabel.toLowerCase()}`}/>;
                    }
                    else if (absType === 'LATE') {
                        setDateTimeErrorMsg(`${endTimeLabel} must be after ${startTimeLabel.toLowerCase()}`);
                        return <LanguageToggle en={`End time minutes: ${endTimeLabel} must be after ${startTimeLabel.toLowerCase()}`} es={`Minutos de hora de fin: ${endTimeLabel} debe ser después de ${startTimeLabel.toLowerCase()}`}/>;
                    }
                }
            }
            return undefined;
        }
        else {
            if (!endTimeMinutes) {
                setDateTimeErrorMsg(`Please select ${endTimeLabel.toLowerCase()} minutes`);
                return <LanguageToggle en={`End time minutes: please select ${endTimeLabel.toLowerCase()} minutes`} es={`Minutos de hora de fin: seleccione los minutos de ${endTimeLabel.toLowerCase()}`}/>;
            }
        }
    };
    const validateLeaveNumberTextInput = (value) => {
        const leaveNumberRegex = /^[1-9]\d*$/;
        if ((value && value.trim() !== '' && !leaveNumberRegex.test(value)) || Number.parseInt(value) > 2147483647) {
            return <LanguageToggle en='Leave number: please enter a valid leave number.' es='Número de permiso: ingrese un número de permiso válido.'/>;
        }
    };
    useEffect(() => {
        setLeaves(leavesData?.map((lve) => {
            return { label: lve.lveNum?.toString(), value: lve.lveNum };
        }) || []);
    }, [leavesData]);
    useEffect(() => {
        if (!!formState.values.hasLeave) {
            getLeaves();
        }
    }, [formState.values.hasLeave]);
    useEffect(() => {
        if (initialAbsence) {
            // If we're editing an absence, pre-fill the inputs
            formApi.setValue('date', formatDate(initialAbsence.uaStartDate));
            formApi.setValue('spansMidnight', initialAbsence.uaSpansMidnight);
            formApi.setValue('absReason', initialAbsence.absReason);
            formApi.setValue('absType', initialAbsence.absType);
        }
        else {
            formApi.reset();
            formApi.setValue('spansMidnight', false);
        }
    }, [initialAbsence]);
    useEffect(() => {
        if (initialAbsence) {
            //Prefill inputs that are dependent on spansMidnight and absType
            if (initialAbsence.uaDuration) {
                formApi.setValue('durationHours', initialAbsence?.uaDuration.substring(0, initialAbsence.uaDuration.indexOf(':')));
                formApi.setValue('durationMinutes', initialAbsence?.uaDuration.substring(initialAbsence?.uaDuration.indexOf(':') + 1, initialAbsence?.uaDuration.length));
            }
            if (formatTime(initialAbsence.uaStartTime) !== 'N/A') {
                formApi.setValue('startTimeHours', moment.utc(initialAbsence.uaStartTime).format('H').padStart(2, '0'));
                formApi.setValue('startTimeMinutes', moment.utc(initialAbsence.uaStartTime).format('mm').padStart(2, '0'));
            }
            if (formatTime(initialAbsence.uaEndTime) !== 'N/A') {
                formApi.setValue('endTimeHours', moment.utc(initialAbsence.uaEndTime).format('H').padStart(2, '0'));
                formApi.setValue('endTimeMinutes', moment.utc(initialAbsence.uaEndTime).format('mm').padStart(2, '0'));
            }
            formApi.validate();
        }
    }, [initialAbsence, formApi.getValue('spansMidnight'), formApi.getValue('absType')]);
    useEffect(() => {
        if (initialAbsence) {
            formApi.setValue('immediateFamily', initialAbsence.uaHasDeath);
            if (['SELF', 'FAMILY'].includes(initialAbsence.absReason)) {
                formApi.setValue('hasLeave', initialAbsence.uaHasLeave);
                if (leavesData) {
                    formApi.setValue('leaveId', initialAbsence.associatedLeave);
                }
                formApi.setValue('leaveIdTextInput', initialAbsence.associatedLeave);
            }
        }
    }, [initialAbsence, formState.values.absReason, leavesData]);
    const Disclaimer = styled.div `
    color: ${themes.rebrand.palette.gray};
    font-style: italic;
  `;
    return (<Container data-testid='testiddd' theme={themes.rebrand}>
      <Row>
        <Col md={6} xs={12}>
          <RadioGroup label='Does your work schedule span midnight?' id='spansMidnight' field='spansMidnight' initialValue={false} notify={['endTimeMinutes']}>
            <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='spansMidnight' id='spansMidnightYes' data-testid='spansMidnightYes' aria-describedby='spansMidnight' label='Yes' value={true}/>
            <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='spansMidnight' data-testid='spansMidnightNo' id='spansMidnightNo' aria-describedby='spansMidnight' label='No' value={false}/>
          </RadioGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6} xs={12}>
          <Label theme={themes.rebrand} style={{ marginBottom: '1rem' }} data-testid={'type-of-time-label'}>
            Please select your type of time off:
          </Label>
          <SelectInput label='Type of time off' semanticLabel='absence Type' data-testid='type-of-time' field='absType' options={UA_TIME_OFF_TYPES} validateOnBlur={true} required={true} errorMessage='Please select a valid type of time off' theme={themes.rebrand} placeholderText='Type of time off'/>
        </Col>
      </Row>
      <Row>
        <Col>
          <Label theme={themes.rebrand} style={{ marginBottom: '1rem' }} data-testid={'absence-date-label'}>
            What is the date of your absence?
          </Label>
        </Col>
      </Row>
      <Row className='mt-0 mb-2'>
        <Col md={9} xs={12}>
          <Disclaimer>
            {`Absences can be submitted for dates between [${format(lowerDateBoundary, 'MM/dd/yyyy')}] 
              and [${format(upperDateBoundary, 'MM/dd/yyyy')}].`}
          </Disclaimer>
        </Col>
      </Row>
      <Row>
        <Col md={6} xs={12}>
          <TextInput data-testid='timeOffDate' field='date' label='Absence date' placeholder={'Absence date'} theme={themes.rebrand} initialValue={format(new Date(), 'MM/dd/yyyy')} validate={validateTimeoffDate} validateOnBlur={true}/>
        </Col>
      </Row>
      {formState.values.spansMidnight === false && formState.values.absType === 'OUT' && (<>
          <Row>
            <Col>
              <Label theme={themes.rebrand} data-testid={'annual-salary-label'}>
                What is the duration of your absence?
              </Label>
            </Col>
          </Row>
          <Row>
            <Col md={3} xs={12}>
              <SelectInput label='Duration hours' data-testid='duration-hours-input' field='durationHours' options={UA_DURATION_HOURS} validateOnBlur={true} required={true} errorMessage='Please select duration hours' notify={['durationMinutes']} theme={themes.rebrand} placeholderText='Duration hours'/>
            </Col>
            <Col md={3} xs={12}>
              <SelectInput label='Duration minutes' data-testid='duration-minutes-input' field='durationMinutes' options={UA_MINUTES} validateOnBlur={true} validateOnChange={true} validate={validateDurationMinutes} errorMessage='Please select duration minutes' theme={themes.rebrand} placeholderText='Duration minutes'/>
            </Col>
          </Row>
        </>)}
      <Relevant when={({ values }) => (values.spansMidnight && values.absType === 'OUT') ||
            ['LATE', 'EARLYNORTN', 'EARLYRTN'].includes(values.absType)}>
        <Row>
          <Col>
            <Label theme={themes.rebrand}>{startTimeQuestion}</Label>
          </Col>
        </Row>
        <Row>
          <Col md={3} xs={12}>
            <SelectInput label={`${startTimeLabel} hours`} data-testid='start-hours-input' field='startTimeHours' options={UA_HOURS} validateOnBlur={true} required={true} errorMessage={`Please select ${startTimeLabel.toLowerCase()} hours`} notify={['endTimeMinutes']} theme={themes.rebrand} placeholderText={`${startTimeLabel} hours`}/>
            <SelectInput label='Duration hours' data-testid='duration-hours-input' field='durationHours' options={UA_DURATION_HOURS} validateOnBlur={true} required={true} errorMessage='Please select duration hours' theme={themes.rebrand} placeholderText='Duration hours'/>
          </Col>
          <Col md={3} xs={12}>
            <SelectInput label={`${startTimeLabel} minutes`} data-testid='start-minutes-input' field='startTimeMinutes' options={UA_MINUTES} validateOnBlur={true} required={true} errorMessage={`Please select ${startTimeLabel.toLowerCase()} minutes`} notify={['endTimeMinutes']} theme={themes.rebrand} placeholderText={`${startTimeLabel} minutes`}/>
          </Col>
        </Row>
        <Row>
          <Col>
            <Label theme={themes.rebrand}>{endTimeQuestion}</Label>
          </Col>
        </Row>
        <Row>
          <Col md={3} xs={12}>
            <SelectInput label={`${endTimeLabel} hours`} data-testid='end-hours-input' field='endTimeHours' options={UA_HOURS} validateOnBlur={true} required={true} errorMessage={`Please select ${endTimeLabel.toLowerCase()} hours`} notify={['endTimeMinutes']} theme={themes.rebrand} placeholderText={`${endTimeLabel} hours`}/>
          </Col>
          <Col md={3} xs={12}>
            <SelectInput label={`${endTimeLabel} minutes`} data-testid='end-minutes-input' aria-label={`Please select ${endTimeLabel} minutes`} field='endTimeMinutes' options={UA_MINUTES} validateOnBlur={true} validateOnChange={true} validate={validateEndMinutes} errorMessage={`Please select ${endTimeLabel.toLowerCase()} minutes`} theme={themes.rebrand} placeholderText={`${endTimeLabel} minutes`}/>
          </Col>
        </Row>
      </Relevant>
      <Row>
        <Col md={6} xs={12}>
          <Label theme={themes.rebrand} style={{ marginBottom: '1rem' }} data-testid={'annual-salary-label'}>
            Please select your reason for time off:
          </Label>
          <SelectInput label='Reason for time off' semanticLabel='absence reason' data-testid='reason-input' field='absReason' options={UA_TIME_OFF_REASONS} validateOnBlur={true} required={true} errorMessage='Please select a reason for time off' theme={themes.rebrand} placeholderText='Reason for time off'/>
        </Col>
      </Row>
      <Relevant when={({ values }) => values.absReason === 'BEREAV'}>
        <Row>
          <Col md={9} xs={12}>
            <RadioGroup label="Are you taking time off due to the death of an immediate family member as defined in the company's Time Off Work policy?" field='immediateFamily' initialValue={false}>
              <Disclaimer data-testid='family-members-disclaimer' className='mb-3'>
                {getEnvVariable('enableNewUsaaUaExperience') ? (<>
                    The following family members qualify as immediate family defined by USAA's Bereavement Time Off
                    policy: Mother, father, mother-in-law, father-in-law, stepmother, stepfather, sister, brother,
                    spouse, domestic partner, domestic partner’s parents, child (including in utero), stepchild,
                    grandchild, grandmother, grandfather, and primary caregiver. Review the Bereavement Time Off policy
                    to determine the number of Bereavement days you are provided. USAA reserves the right to request
                    reasonable documentation to substantiate the loss of an "immediate family member".
                  </>) : (<>
                    The following family members qualify as immediate family defined by USAA's Time Off Work policy:
                    Mother, father, mother-in-law, father-in-law, stepmother, stepfather, sister, brother, spouse,
                    child, stepchild, grandmother, grandfather, and primary caregiver.
                  </>)}
              </Disclaimer>
              <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='immediateFamily' data-testid='immediateFamilyYes' id='immediateFamilyYes' label='Yes' value={true}/>
              <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='immediateFamily' data-testid='immediateFamilyNo' id='immediateFamilyNo' label='No' value={false}/>
            </RadioGroup>
          </Col>
        </Row>
      </Relevant>
      <Relevant when={({ values }) => ['SELF', 'FAMILY'].includes(values.absReason) && !getEnvVariable('enableNewUsaaUaExperience')}>
        <Row>
          <Col md={6} xs={12}>
            <RadioGroup label='Is this absence related to an existing Family Medical Leave or State Leave?' field='hasLeave' initialValue={false}>
              <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='hasLeave' data-testid='hasLeaveYes' id='hasLeaveYes' label='Yes' value={true}/>
              <Radio theme={themes.rebrand} containerStyle={{ marginBottom: '.5rem' }} name='hasLeave' data-testid='hasLeaveNo' id='hasLeaveNo' label='No' value={false}/>
            </RadioGroup>
          </Col>
        </Row>
      </Relevant>
      <Relevant when={({ values }) => values.hasLeave === true}>
        <Loader isShown={leavesLoading} data-testid='leave-loader'>
          {!leavesLoading && !leavesError && leaves.length > 0 && (<Row>
              <Col md={6} xs={12}>
                <Label theme={themes.rebrand} style={{ marginBottom: '1rem' }} data-testid={'select-leave-id-label'}>
                  Please select a the related leave:
                </Label>
                <SelectInput aria-label='Select Leave ID' data-testid='leave-id-input' data-private={true} field='leaveId' label='Leave ID' options={leaves} placeholder='Leave ID' theme={themes.rebrand}/>
              </Col>
            </Row>)}
          {!leavesLoading && (leavesError || leaves?.length === 0) && (<Row>
              <Col md={6} xs={12} className={'mt-2'}>
                <Label theme={themes.rebrand} style={{ marginBottom: '1rem' }} data-testid={'text-input-leave-id-label'}>
                  Enter a related leave if applicable (optional):
                </Label>
                <TextInput ariaLabelText='Enter the leave ID' data-testid='leave-id-textinput' data-private={true} field='leaveIdTextInput' label='Leave ID' validateOnBlur={true} validate={validateLeaveNumberTextInput} placeholder={'Leave ID'} theme={themes.rebrand}/>
              </Col>
            </Row>)}
        </Loader>
      </Relevant>

      <Row>
        <Col md={3} xs={12}>
          <Button type='submit' data-testid='submit-btn' buttonType='primary' theme={themes.rebrand} className='w-100 mt-2'>
            Submit
          </Button>
        </Col>
        <Col md={3} xs={12}>
          {hasAbsences && (<Button buttonType={'secondary'} data-testid='cancel-btn' onClick={onCancel} theme={themes.rebrand} className='w-100 mt-2'>
              Cancel
            </Button>)}
        </Col>
      </Row>
    </Container>);
};
export default UnplannedAbsenceForm;
